import { useState, useCallback, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { ePath, isEntrepreneurPath } from '@/utils/paths';
import { Bell, Check, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { useNotifications } from '@/hooks/useNotifications';
import { useNotificationStore } from '@/stores/notificationStore';
import { NotificationItem } from './NotificationItem';
import { cn } from '@/lib/utils';

function ShootingStar({
  from,
  to,
  onDone,
}: {
  from: { x: number; y: number };
  to: { x: number; y: number };
  onDone: () => void;
}) {
  const [style, setStyle] = useState<React.CSSProperties>({
    position: 'fixed',
    zIndex: 9999,
    left: from.x - 8,
    top: from.y - 8,
    width: 16,
    height: 16,
    borderRadius: '50%',
    background: 'hsl(var(--primary))',
    boxShadow:
      '0 0 12px 4px hsl(var(--primary) / 0.6), 0 0 24px 8px hsl(var(--primary) / 0.3)',
    transition: 'all 450ms cubic-bezier(0.4, 0, 0.2, 1)',
    opacity: 1,
    transform: 'scale(1)',
    pointerEvents: 'none' as const,
  });

  useEffect(() => {
    requestAnimationFrame(() => {
      setStyle((prev) => ({
        ...prev,
        left: to.x - 8,
        top: to.y - 8,
        transform: 'scale(0.3)',
        opacity: 0.4,
      }));
    });

    const timer = setTimeout(onDone, 500);
    return () => clearTimeout(timer);
  }, [to.x, to.y, onDone]);

  return createPortal(
    <div style={style}>
      <Loader2 className="h-3 w-3 text-primary-foreground animate-spin" />
    </div>,
    document.body,
  );
}

function BellImpact({
  center,
  onDone,
}: {
  center: { x: number; y: number };
  onDone: () => void;
}) {
  useEffect(() => {
    const timer = setTimeout(onDone, 600);
    return () => clearTimeout(timer);
  }, [onDone]);

  return createPortal(
    <div
      style={{
        position: 'fixed',
        zIndex: 9998,
        left: center.x - 16,
        top: center.y - 16,
        width: 32,
        height: 32,
        borderRadius: '50%',
        border: '2px solid hsl(var(--primary))',
        animation: 'bell-pulse-ring 600ms ease-out forwards',
        pointerEvents: 'none',
      }}
    />,
    document.body,
  );
}

export function NotificationBell() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();

  const bellAnimating = useNotificationStore((s) => s.bellAnimating);
  const setBellAnimating = useNotificationStore((s) => s.setBellAnimating);
  const shootingStarOrigin = useNotificationStore((s) => s.shootingStarOrigin);
  const clearShootingStar = useNotificationStore((s) => s.clearShootingStar);

  const bellRef = useRef<HTMLButtonElement>(null);
  const [starTarget, setStarTarget] = useState<{ x: number; y: number } | null>(null);
  const [showImpact, setShowImpact] = useState(false);
  const [activeStarFrom, setActiveStarFrom] = useState<{ x: number; y: number } | null>(null);

  useEffect(() => {
    if (!shootingStarOrigin) return;

    const rect = bellRef.current?.getBoundingClientRect();
    const target = rect
      ? { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 }
      : { x: window.innerWidth - 60, y: 28 };

    setActiveStarFrom(shootingStarOrigin);
    setStarTarget(target);
    clearShootingStar();
  }, [shootingStarOrigin, clearShootingStar]);

  const handleStarDone = useCallback(() => {
    setActiveStarFrom(null);
    setShowImpact(true);
  }, []);

  const handleImpactDone = useCallback(() => {
    setShowImpact(false);
    setBellAnimating(true);
  }, [setBellAnimating]);

  const visibleNotifications = notifications.slice(0, 5);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (open) {
      setBellAnimating(false);
    }
  };

  const impactCenter = starTarget || { x: window.innerWidth - 60, y: 28 };

  return (
    <>
      {activeStarFrom && starTarget && (
        <ShootingStar from={activeStarFrom} to={starTarget} onDone={handleStarDone} />
      )}
      {showImpact && <BellImpact center={impactCenter} onDone={handleImpactDone} />}

      <Popover open={isOpen} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button
            ref={bellRef}
            variant="ghost"
            size="icon"
            className="relative"
            aria-label={t('notifications.bell')}
            data-notification-bell
          >
            <Bell
              className="h-5 w-5"
              style={{
                animation: bellAnimating ? 'bell-ring 0.8s ease-in-out' : 'none',
                transformOrigin: 'top center',
              }}
              onAnimationEnd={() => setBellAnimating(false)}
            />
            {unreadCount > 0 && (
              <span
                className={cn(
                  'absolute -top-0.5 -right-0.5 flex items-center justify-center',
                  'min-w-[18px] h-[18px] px-1 rounded-full',
                  'bg-destructive text-destructive-foreground',
                  'text-[10px] font-medium'
                )}
              >
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-0" align="end">
          <div className="flex items-center justify-between px-4 py-3">
            <h4 className="text-sm font-semibold">
              {t('notifications.title')}
            </h4>
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="h-auto px-2 py-1 text-xs"
                onClick={() => markAllAsRead()}
              >
                <Check className="h-3 w-3 mr-1" />
                {t('notifications.markAllRead')}
              </Button>
            )}
          </div>
          <Separator />
          <ScrollArea className="max-h-[300px]">
            {visibleNotifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                <Bell className="h-8 w-8 mb-2 opacity-50" />
                <p className="text-sm">
                  {t('notifications.empty')}
                </p>
              </div>
            ) : (
              <div className="divide-y">
                {visibleNotifications.map((notification) => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    onMarkAsRead={markAsRead}
                    onClick={() => setIsOpen(false)}
                  />
                ))}
              </div>
            )}
          </ScrollArea>
          {notifications.length > 5 && (
            <>
              <Separator />
              <div className="p-2">
                <Button
                  variant="ghost"
                  className="w-full text-sm"
                  onClick={() => {
                    setIsOpen(false);
                    // `/notifications` is mounted twice and in two different
                    // places: inside each B2C portal, and once at the host
                    // root for staff. `tPath` pointed at neither on a
                    // non-terminus host, so "view all" landed on NotFound.
                    navigate(
                      isEntrepreneurPath()
                        ? ePath('/notifications')
                        : '/notifications',
                    );
                  }}
                >
                  {t('notifications.viewAll')}
                </Button>
              </div>
            </>
          )}
        </PopoverContent>
      </Popover>
    </>
  );
}
